"use client";
import ListCard from "@/components/views/list/card/VenueCard";
import { useEffect, useMemo, useState, useRef } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import { VenueLoading } from "@/components/views/list/venue-list/Venue";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import apiFetch from "@/lib/fetch";
import { buildVenueSearchRoute } from "@/lib/venue-search-url";
import { useNearbyVenue } from "@/hooks/nearbyVenueContext";
import { firstCharUpper } from "@/lib/utils";
import NotFoundNearbyVenues from "@/components/views/list/close-venues/NotFoundNearbyVenues";
import SeeMoreNearby from "@/components/views/list/see-more/SeeMoreNearby";
import { useHeader } from "@/hooks/headerContext";
import { useClientTranslation } from "@/app/i18n/client";
import { useTranslation } from "@/hooks/translationContext";
import Ads from "@/components/ads";
import { useUserLocation } from "@/context/UserLocationContext";
import LocationPermissionModal from "@/components/views/list/LocationPermissionModal";
import { useCurrentDeviceContext } from "@/context/DeviceContext";
import LocationToggleBar from "@/components/views/list/LocationToggleBar";

interface Props {
  data: any[];
  nearbyData?: Venue.List[];
  service?: string;
  district?: string;
  venue?: string;
  filterMobileAds?: any;
  filterDesktopAds?: any;
}

export default function VenueClientSection(props: Props) {
  const [data, setData] = useState<any>(props.data);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const {
    locationPermission,
    userLocation,
    venueDistances,
    setShowPermissionModal,
    calculateDistances,
    sortByDistance,
  } = useUserLocation();

  const { device } = useCurrentDeviceContext();
  const isMobile = device === "mobile";

  const hasPromptedForLocation = useRef(false);
  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Mobile only: show location permission modal on first load
  useEffect(() => {
    if (!isMobile) return;

    if (locationPermission === "prompt" && !hasPromptedForLocation.current && typeof window !== "undefined") {
      hasPromptedForLocation.current = true;
      const timer = setTimeout(() => {
        setShowPermissionModal(true);
      }, 1500);
      return () => clearTimeout(timer);
    }

    // Show feedback modal when permission is denied. "unavailable" is not popped
    // here: a quiet background lookup that fails must not interrupt the user; when
    // the user asked for the location the context opens the modal itself.
    if (locationPermission === "denied") {
      setShowPermissionModal(true);
    }
  }, [locationPermission, setShowPermissionModal, isMobile]);

  const nearbyData = props.nearbyData;

  // Calculate distances when user location is available and new venues are loaded
  useEffect(() => {
    if (locationPermission === "granted" && userLocation) {
      const mainVenues: Venue.List[] = Array.isArray(data.response) ? data.response : [];
      const venuesToCalculate = [...mainVenues, ...(nearbyData || [])]
        .filter((venue: Venue.List) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: Venue.List) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        // Mark these venues as being calculated
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, data.response, nearbyData, calculateDistances]);

  const sortedVenues = useMemo(() => {
    const mainVenues: Venue.List[] = Array.isArray(data.response) ? data.response : [];
    if (!sortByDistance) return mainVenues;

    const seen = new Set<number>();
    const combined: Venue.List[] = [];
    for (const v of [...mainVenues, ...(nearbyData || [])]) {
      if (!seen.has(v.id)) {
        seen.add(v.id);
        combined.push(v);
      }
    }

    if (venueDistances.size === 0) return combined;

    return [...combined].sort((a, b) => {
      const da = venueDistances.get(a.id);
      const db = venueDistances.get(b.id);
      if (da == null && db == null) return 0;
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  }, [data.response, nearbyData, sortByDistance, venueDistances]);

  // Yakınımdakiler açıkken liste backend'de mesafeye göre sıralanıp sayfalanır;
  // yalnızca yüklenmiş sayfaları burada sıralamak, sonraki sayfalardaki daha
  // yakın mekanları kullanıcı aşağı kaydırana kadar gizliyordu.
  const distanceLat = sortByDistance && userLocation ? userLocation.lat : null;
  const distanceLng = sortByDistance && userLocation ? userLocation.lng : null;
  const distanceLocation =
    distanceLat != null && distanceLng != null
      ? { lat: distanceLat, lng: distanceLng }
      : null;

  // Sıralama değiştiğinde önceki sıralamaya ait geç gelen cevapları yok say
  const requestIdRef = useRef(0);

  const next = (page: number) => {
    if (isLoading) return;

    const url = buildVenueSearchRoute({
      page,
      service: props.service,
      district: props.district,
      venue: props.venue,
      location: distanceLocation,
    });

    const requestId = requestIdRef.current;
    setIsLoading(true);
    apiFetch({ route: url }).then((res) => {
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
      const response = Array.isArray(res?.response) ? res.response : [];
      if (response.length === 0) setHasMore(false);

      setData((prev: any) => {
        return {
          response: [...prev.response, ...response],
          summary: prev?.summary,
        };
      });
    });
  };

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (distanceLat == null || distanceLng == null) {
      setIsLoading(false);
      setData(props.data);
      const responseLength = (props.data as any).response?.length;

      if (responseLength < DEFAULT_DATA_LIMIT) setHasMore(false);
      else setHasMore(true);
      return;
    }

    // Listeyi baştan, kullanıcıya en yakın mekandan başlayarak yükle
    setIsLoading(true);
    apiFetch({
      route: buildVenueSearchRoute({
        page: 0,
        service: props.service,
        district: props.district,
        venue: props.venue,
        location: { lat: distanceLat, lng: distanceLng },
      }),
    }).then((res) => {
      if (requestId !== requestIdRef.current) return;
      setIsLoading(false);
      // İstek başarısız olursa ilk sayfaya dön (yalnızca istemcide sıralanır)
      if (!Array.isArray(res?.response)) {
        setData(props.data);
        setHasMore((props.data as any).response?.length >= DEFAULT_DATA_LIMIT);
        return;
      }

      setData((prev: any) => ({
        response: res.response,
        summary: res.summary ?? prev?.summary,
      }));
      setHasMore(res.response.length >= DEFAULT_DATA_LIMIT);
    });
  }, [props.data, props.service, props.district, props.venue, distanceLat, distanceLng]);

  return (
    <InfiniteScroll
      // Sıralama değişince sayfa sayacı sıfırdan başlasın
      key={distanceLocation ? `distance-${distanceLat}-${distanceLng}` : "default"}
      skeletonElementId="venue-card-skeleton"
      isLoading={isLoading}
      hasMore={hasMore}
      loading={
        <>
          <VenueLoading count={6} />
        </>
      }
      next={next}
      dataLength={data.response?.length || 0}
    >
      <LocationPermissionModal />
      {!isMobile && <LocationToggleBar />}
      <section
        id="venues"
        className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-3 gap-5 py-4"
      >
        {sortedVenues.map((venue: Venue.List, index) => {
            const distance = venueDistances.get(venue.id);
            return (
              <>
                <ListCard
                  firstPriority={index == 0}
                  titleHeadingLevel="h3"
                  key={venue.id}
                  distance={distance}
                  {...venue}
                  index={index}
                  serviceSlug={props.service}
                />
                {index === 2 && (props.filterDesktopAds?.length > 0 || props.filterMobileAds?.length > 0) && (
                  <div className="w-full xl:col-span-3 my-4">
                    {props.filterDesktopAds?.length > 0 && (
                      <Ads
                        deviceType="desktop"
                        placement="filter-desktop-mid"
                        initialData={props.filterDesktopAds}
                      />
                    )}
                    {props.filterMobileAds?.length > 0 && (
                      <Ads
                        deviceType="mobile"
                        placement="filter-mobile-mid"
                        initialData={props.filterMobileAds}
                      />
                    )}
                  </div>
                )}
              </>
            );
          })}
      </section>
    </InfiniteScroll>
  );
}
